import React, { useEffect, useState } from 'react';

type Theme = 'daylight' | 'moonlight';

const STORE = 'ithildin-theme';
const DEFAULT: Theme = 'moonlight'; // dark default

const GLYPH: Record<Theme, string> = { moonlight: '☾', daylight: '☀' };
const LABEL: Record<Theme, string> = { moonlight: 'Moonlight', daylight: 'Daylight' };

// ── shared theme store ───────────────────────────────────────
// A tiny module-level store so every control (the desktop segmented switch and
// the mobile single-icon toggle) stays in sync — flipping one updates them all.
function read(): Theme {
  if (typeof window === 'undefined') return DEFAULT;
  const s = localStorage.getItem(STORE);
  return s === 'daylight' || s === 'moonlight' ? s : DEFAULT;
}
let current: Theme = read();
const listeners = new Set<() => void>();

export function setTheme(next: Theme) {
  current = next;
  if (typeof document !== 'undefined') document.documentElement.setAttribute('data-theme', next);
  try { localStorage.setItem(STORE, next); } catch { /* ignore */ }
  listeners.forEach(fn => fn());
}
export function toggleTheme() {
  setTheme(current === 'moonlight' ? 'daylight' : 'moonlight');
}

export function useTheme(): Theme {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force(n => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return current;
}

// ── desktop: segmented moon | sun switch ─────────────────────
export function ThemeSwitcher() {
  const theme = useTheme();
  return (
    <div className="theme-switcher" role="group" aria-label="Theme">
      {(['moonlight', 'daylight'] as Theme[]).map(id => (
        <button
          key={id}
          onClick={() => setTheme(id)}
          className={theme === id ? 'active' : ''}
          title={LABEL[id]}
          aria-label={LABEL[id]}
          aria-pressed={theme === id}
        >
          {GLYPH[id]}
        </button>
      ))}
    </div>
  );
}

// ── mobile: single icon that swaps sun/moon on tap ───────────
export function ThemeToggleButton({ className }: { className?: string }) {
  const theme = useTheme();
  const next = theme === 'moonlight' ? 'daylight' : 'moonlight';
  return (
    <button
      className={`theme-toggle ${className ?? ''}`.trim()}
      onClick={toggleTheme}
      title={`Switch to ${LABEL[next]}`}
      aria-label={`Switch to ${LABEL[next]} theme`}
    >
      {GLYPH[theme]}
    </button>
  );
}
